import React, { FC, useMemo } from 'react';
import clsx from 'clsx';
import * as Styled from './OHighlight.styles';
import {
  OHighlightButton,
  OHighlightContentAlignment,
  OHighlightIconItem,
  OHighlightNumericItem,
  OHighlightProps,
  OHighlightTextItem,
  OHighlightType,
} from './OHighlight.types';
import { MHighlightIcon } from '../../molecules/MHighlightIcon/index';
import { MHighlightNumeric } from '../../molecules/MHighlightNumeric/index';
import { MHighlightText } from '../../molecules/MHighlightText/index';
import { MButtonType, MButtonSize } from '../../molecules/MButton';
import { MButtonProps } from '../../molecules/MButton/MButton.types';
import { MHighlightIconAlignment } from '../../molecules/MHighlightIcon/MHighlightIcon.types';

const COMPONENT_NAME = 'o-mi-web-ui-kit-OHighlight';
const TEST_ID = 'mi-web-ui-kit-OHighlight';

const MAX_ITEMS_IN_A_COLUMN = 6;

export const OHighlight: FC<OHighlightProps> = ({
  componentId,
  type,
  mHighlightIcons = [],
  mHighlightNumerics = [],
  mHighlightTexts = [],
}) => {
  const highlightItems = useMemo(() => {
    switch (type) {
      case OHighlightType.Icon:
        return mHighlightIcons
          .slice(0, MAX_ITEMS_IN_A_COLUMN)
          .map((mHighlightIcon, index) => renderHighlightIcon(mHighlightIcon, index));
      case OHighlightType.Numeric:
        return mHighlightNumerics
          .slice(0, MAX_ITEMS_IN_A_COLUMN)
          .map((mHighlightNumeric, index) => renderHighlightNumeric(mHighlightNumeric, index));
      case OHighlightType.Text:
        return mHighlightTexts
          .slice(0, MAX_ITEMS_IN_A_COLUMN)
          .map((mHighlightText, index) => renderHighlightText(mHighlightText, index));
      default:
        return [];
    }
  }, [type, mHighlightIcons, mHighlightNumerics, mHighlightTexts]);
  const numItems = highlightItems.length;

  return (
    <Styled.Wrapper
      data-component-name={COMPONENT_NAME}
      data-testid={TEST_ID}
      className={clsx('container d-flex justify-content-center align-items-center py-5')}
    >
      <Styled.Grid
        numItems={numItems}
        data-componentid={componentId}
        className={clsx('col-12', numItems === 5 && 'col-lg-10', 'px-0')}
      >
        {highlightItems}
      </Styled.Grid>
    </Styled.Wrapper>
  );
};

function renderHighlightIcon(mHighlightIcon: OHighlightIconItem, index: number) {
  const { alignment, aIcon, aSubheader, aBody, mButton } = mHighlightIcon;
  if (!aSubheader?.text || !aIcon?.icon) {
    return null;
  }
  return (
    <MHighlightIcon
      key={`highlight-icon-${index}`}
      alignment={alignment ?? MHighlightIconAlignment.Centered}
      aIcon={aIcon}
      aSubheader={aSubheader}
      aBody={aBody}
      showCTA={!!mButton?.buttonText}
      mButton={extractButtonProps(mButton)}
    />
  );
}

function renderHighlightNumeric(mHighlightNumeric: OHighlightNumericItem, index: number) {
  const { aBody, aDigit, mButton } = mHighlightNumeric;
  if (!aDigit?.text) {
    return null;
  }
  return (
    <MHighlightNumeric
      key={`highlight-numeric-${index}`}
      hasAccentLine={true}
      aDigit={aDigit}
      aBody={aBody}
      showCTA={!!mButton?.buttonText}
      mButton={extractButtonProps(mButton)}
    />
  );
}

function renderHighlightText(mHighlightText: OHighlightTextItem, index: number) {
  const { aBody, aSubheader, mButton } = mHighlightText;
  if (!aSubheader?.text) {
    return null;
  }
  return (
    <MHighlightText
      key={`highlight-text-${index}`}
      hasAccentLine={true}
      aSubheader={aSubheader}
      aBody={aBody}
      showCTA={!!mButton?.buttonText}
      mButton={extractButtonProps(mButton)}
    />
  );
}

function extractButtonProps(mButton: OHighlightButton | undefined): MButtonProps & { buttonText?: string } {
  return {
    ...(mButton?.buttonText ? { buttonText: mButton?.buttonText } : {}),
    ...(mButton?.type
      ? {
          variant: mButton?.type as MButtonType,
          size: mButton?.type === MButtonType.Tertiary ? undefined : (mButton?.size as MButtonSize),
        }
      : {}),
    ...(mButton?.ctaLink ? { href: mButton?.ctaLink, isExternal: mButton?.openInANewTab === 'true' } : {}),
  };
}

export { OHighlightContentAlignment, OHighlightType, TEST_ID as O_HIGHLIGHT_TEST_ID };
export type { OHighlightProps };
